import { styleConstants } from '@paywithextend/components'
import cn from 'classnames'
import DecliningBalanceFlyoutSvg from 'ext-ui/svg/illustration/DecliningBalanceFlyoutSvg'
import React from 'react'
import { useSelector } from 'react-redux'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import Box from 'src/components/Box/Box'
import ChooseObjectTypeBox from 'src/components/ChooseObjectTypeBox/ChooseObjectTypeBox'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { useQueryOrganizationVirtualCardTypes } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import styled from 'styled-components'

import style from './UpdateVirtualCardPickContent.style'

interface UpdateVirtualCardPickContentProps {
  className?: string
  id: string
  setScreen: (val: UpdateVirtualCardScreen) => void
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions: VirtualCardPermissionsModel
  hideRecurring: boolean
}

const ChooseUpdateObjectTypeBox = styled(ChooseObjectTypeBox)`
  &:nth-child(n + 2) {
    margin-top: ${styleConstants.heights.minorInternalGutter};
  }
`

const UpdateVirtualCardPickContent: React.FC<
  UpdateVirtualCardPickContentProps
> = (props) => {
  const translate = useTranslate()
  const cardTypeStandardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const cardTypeRecurringImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeRecurring,
  )
  const user = useCurrentUser()
  const enableServerCardTypes =
    useSelector(selectFeatureFlaggerFromState)?.enable_server_card_types ??
    false
  const { data: virtualCardTypes } = useQueryOrganizationVirtualCardTypes({
    orgId: user?.organizationId,
    enabled: enableServerCardTypes,
  })

  const showStandard = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.standard)
    : true
  const showRecurring = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.recurring) &&
      !props.hideRecurring
    : !props.hideRecurring
  const showDecliningBalance = enableServerCardTypes
    ? virtualCardTypes?.includes(VirtualCardTypes.decliningBalance)
    : false

  return (
    <ActionRightFlyoutContent
      css={style}
      className={cn('UpdateVirtualCardPickContent', props.className)}
      title={props.virtualCard.updateFlyoutTitle(props.virtualCardPermissions)}
      pickerSubtitle={
        <>
          {translate(
            'Change the card type to what is needed. Note that changing the type will affect what form fields you see.',
          )}
        </>
      }
      permissions={{
        canUpdateVirtualCard:
          props.virtualCardPermissions.canUpdateVirtualCard &&
          !props.virtualCard.isConsumed,
      }}
    >
      {showStandard && (
        <ChooseUpdateObjectTypeBox
          imageSrc={cardTypeStandardImage}
          title={<>{translate('Standard Card')}</>}
          subtitle={
            props.virtualCardPermissions.canUpdateVirtualCard ? (
              <>
                {translate(
                  'Create a basic virtual card with all the set controls you need to securely send a company card.',
                )}
              </>
            ) : (
              <>
                {translate(
                  'The most common option. Use it for day-to-day expenses, or just once. You can receive or request future updates when needed.',
                )}
              </>
            )
          }
          onClick={(): void => {
            props.setScreen(UpdateVirtualCardScreen.Standard)
          }}
        />
      )}
      {showRecurring && (
        <ChooseUpdateObjectTypeBox
          imageSrc={cardTypeRecurringImage}
          title={<>{translate('Recurring Card')}</>}
          subtitle={
            props.virtualCardPermissions.canUpdateVirtualCard ? (
              <>
                {translate(
                  'Set up a card for recurring expenses at a set frequency, such as monthly or weekly subscriptions.',
                )}
              </>
            ) : (
              <>
                {translate(
                  'Have a recurring expense? This virtual card will reset its spend limit at a daily, weekly or monthly frequency.',
                )}
              </>
            )
          }
          onClick={(): void => {
            props.setScreen(UpdateVirtualCardScreen.Recurring)
          }}
        />
      )}
      {showDecliningBalance && (
        <Box
          className="declining-balance-box"
          onClick={(): void => {
            props.setScreen(UpdateVirtualCardScreen.DecliningBalance)
          }}
        >
          <DecliningBalanceFlyoutSvg className="declining-balance-image" />
          <div className="declining-balance-text">
            <span className="declining-balance-title">
              {translate('Declining Balance Card')}
              <SvgChevronRight />
            </span>
            <span className="declining-balance-subtitle">
              {translate(
                'Create a virtual card with a fixed spending limit. The available balance decreases as purchases are made and the card remains active until the date you set.',
              )}
            </span>
          </div>
        </Box>
      )}
    </ActionRightFlyoutContent>
  )
}

export default UpdateVirtualCardPickContent
